import { AxiosResponse } from "axios";
import { deleteRequest, getRequest, postRequest } from "../../utils/httpRequest";
import { pushDataLayer } from "../../utils/analytics";

export type Prize = "pct13" | "pct20" | "guide10" | "pct42";
export type Trick = "cards" | "riddle";

export interface Guide {
  slug: string;
  course_name: string;
  title: string;
  line?: string;
  url?: string;
}

export interface Play {
  path: "trick" | "treat";
  trick: Trick | null;
  prize: Prize;
  percent: number;
  guide_prize: boolean;
  code: string;
  guide: Guide | null;
}

export interface HalloweenState {
  running: boolean;
  // The year's name, e.g. "halloween-2026". Null before any year is set up.
  key: string | null;
  ends_at: string | null;
  popup: boolean;
  // Test mode: admins, and anyone running the site on their own machine.
  tester: boolean;
  wheel: Prize[];
  // What each prize is worth this year, set in admin.
  prizes: Record<Prize, number>;
  guides: Guide[];
  play: Play | null;
}

export const prizeLabel = (prize: Prize, prizes: Record<Prize, number>) =>
  prize === "guide10" ? `Tips booklet + ${prizes[prize]}% off` : `${prizes[prize]}% off`;

// The closing time in UK time, however the visitor's clock is set:
// "11.59pm", "Sunday", "1 November 2026", and "Sunday night" for a year that
// stops late in the evening ("Monday at 9am" for one that doesn't).
export const closing = (endsAt: string | null) => {
  const date = endsAt ? new Date(endsAt) : null;
  if (!date || Number.isNaN(date.getTime())) return null;

  const uk = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", ...options });
  const parts = uk({ hour: "numeric", minute: "2-digit", hour12: true }).formatToParts(date);
  const part = (type: string) => parts.find((each) => each.type === type)?.value || "";
  const minute = part("minute");
  const time = `${part("hour")}${minute === "00" ? "" : `.${minute}`}${part("dayPeriod").toLowerCase()}`;
  const weekday = uk({ weekday: "long" }).format(date);
  const hour = Number(uk({ hour: "numeric", hourCycle: "h23" }).format(date));

  return {
    time,
    weekday,
    date: uk({ day: "numeric", month: "long", year: "numeric" }).format(date),
    words: hour >= 21 ? `${weekday} night` : `${weekday} at ${time}`,
  };
};

const URL = "/api/halloween.json";

export const fetchHalloween = () =>
  getRequest(URL).then((response: AxiosResponse<HalloweenState>) => response.data);

// `prize` is test mode's "land on this"; the server ignores it for everyone
// else.
export const playHalloween = (path: "trick" | "treat", trick?: Trick, prize?: Prize) =>
  postRequest(URL, { path, trick, prize }).then(
    (response: AxiosResponse<{ play: Play; slice: number | null; fresh: boolean }>) => response.data,
  );

export const replayHalloween = () => deleteRequest(URL);

export const chooseGuide = (course: string) =>
  postRequest("/api/halloween/guide.json", { course }).then(
    (response: AxiosResponse<{ play: Play }>) => response.data.play,
  );

// One event name for the whole game, told apart by `action`, so GTM needs a
// single trigger to see how far people get: open, play, prize, guide, copy,
// share.
export const trackHalloween = (action: string, details: Record<string, unknown> = {}) =>
  pushDataLayer({ event: "halloween", action, ...details });

export const HALLOWEEN_URL = "https://www.42courses.com/halloween";
